/**
 * scoreTags — derive UK-anchored tag copy from the scoring engine's outputs.
 *
 * Replaces the numeric ScoreBadge / 0-100 row values with descriptive pills.
 * Pure functions; no React, no I/O. Hardcoded tables (per plan): copy and
 * thresholds live here. Calibration target (Spiegel/Northwestern sweet spot,
 * Which?/AutoTrader UK voice anchors): ~30/45/20/5 overall tier and
 * ~35/45/20 across evaluative signals on a positively-selected feed.
 *
 * Public API:
 *   getSignalTag(signal, value, explanation, snapshot) -> Tag | null
 *   getOverallTag(listing, cohort) -> { tier, modifier }
 *
 * Tag shape: { label: string, valence: 'positive'|'neutral'|'negative', informational?: boolean }
 * A null return from getSignalTag means "suppress this row" (e.g. price_trend Stable).
 */

// -------------------------------------------------------------------
// Per-signal banding — declarative tables for pure value -> label
// -------------------------------------------------------------------

const SIGNAL_BANDS = {
  price_vs_market: [
    { min: 80, label: 'Great price',  valence: 'positive' },
    { min: 56, label: 'Good price',   valence: 'neutral'  },
    { min: 0,  label: 'Priced above', valence: 'negative' },
  ],
  mileage: [
    { min: 62, label: 'Low mileage',     valence: 'positive' },
    { min: 36, label: 'Average mileage', valence: 'neutral'  },
    { min: 0,  label: 'High mileage',    valence: 'negative' },
  ],
  distance: [
    { min: 90, label: 'Nearby',       valence: 'positive' },
    { min: 21, label: 'Within range', valence: 'neutral'  },
    { min: 0,  label: 'Far',          valence: 'negative' },
  ],
  year_match: [
    { min: 100, label: 'Recent year', valence: 'positive' },
    { min: 76,  label: 'In range',    valence: 'neutral'  },
    { min: 0,   label: 'Older model', valence: 'negative' },
  ],
  trim_match: [
    { min: 90, label: 'Matching spec',  valence: 'positive' },
    { min: 50, label: 'Similar spec',   valence: 'neutral'  },
    { min: 0,  label: 'Different spec', valence: 'negative' },
  ],
  transmission_match: [
    { eq: 100, label: 'Auto',   valence: 'positive', informational: true },
    { eq: 75,  label: 'Manual', valence: 'neutral',  informational: true },
  ],
  ulez: [
    { eq: 100, label: 'ULEZ compliant',     valence: 'positive', informational: true },
    { eq: 0,   label: 'Not ULEZ compliant', valence: 'negative' },
  ],
};

// -------------------------------------------------------------------
// Per-signal resolvers — when copy depends on snapshot or explanation
// -------------------------------------------------------------------

const SIGNAL_RESOLVERS = {
  /**
   * dom — U-curve. The engine's explanation prefix already classifies:
   *   "New listing: ..."             -> Just listed (positive)
   *   "... typical for this model"   -> On the market (neutral)
   *   "Listed ... likely negotiable" -> Likely negotiable (positive)
   *   "Listed ... may be negotiable" -> Likely negotiable (positive)
   *   else fallback by value
   */
  dom: (value, explanation) => {
    const exp = (explanation || '').toLowerCase();
    if (exp.startsWith('new listing')) {
      return { label: 'Just listed', valence: 'positive' };
    }
    if (exp.includes('negotiable')) {
      return { label: 'Likely negotiable', valence: 'positive' };
    }
    if (value == null) return null;
    if (value >= 81) return { label: 'Just listed',     valence: 'positive' };
    if (value >= 46) return { label: 'On the market',   valence: 'neutral'  };
    return              { label: 'Long-listed',         valence: 'negative' };
  },

  /**
   * fuel_match — informational verdict. Positive is "Fuel match" (not the
   * snapshot fuel type, which is already shown on the left as the data point —
   * echoing it on the right is redundant). Negative is "Different fuel"
   * (research: less blunt than "Wrong fuel" which reduces trust).
   */
  fuel_match: (value) => {
    if (value === 100) return { label: 'Fuel match',     valence: 'positive', informational: true };
    if (value === 0)   return { label: 'Different fuel', valence: 'negative' };
    return null;
  },

  /**
   * owners — interprets snapshot.num_owners as ownership pattern. V5C
   * terminology: "previous keeper" not "owner". The left column already shows
   * the count; this tag describes the pattern (property-descriptive language
   * for neutral bands so the badge does its own work, not echo the count).
   */
  owners: (_value, _explanation, snapshot) => {
    const raw = snapshot && snapshot.num_owners;
    const n = raw == null ? null : Number(raw);
    if (!n || n <= 0) return null;
    if (n === 1) return { label: 'One previous keeper', valence: 'positive', informational: true };
    if (n === 2) return { label: 'Typical history',     valence: 'neutral',  informational: true };
    if (n === 3) return { label: 'Some turnover',       valence: 'neutral',  informational: true };
    return         { label: 'Concerning',               valence: 'negative' };
  },

  /**
   * price_trend — informational. Suppress "Stable" (returns null -> row hidden).
   * Engine emits 100/75 for drops, 50 for stable, 25 for increases.
   */
  price_trend: (value) => {
    if (value == null) return null;
    if (value >= 75) return { label: 'Recent drop',     valence: 'positive', informational: true };
    if (value <= 30) return { label: 'Recent increase', valence: 'negative', informational: true };
    return null;
  },
};

// -------------------------------------------------------------------
// Overall tier — declarative
// -------------------------------------------------------------------

const TIER_BANDS = [
  { min: 72, key: 'strong',  label: 'Strong match'  },
  { min: 63, key: 'solid',   label: 'Solid match'   },
  { min: 50, key: 'mixed',   label: 'Mixed match'   },
  { min: 0,  key: 'limited', label: 'Limited match' },
];

// -------------------------------------------------------------------
// Modifier precedence — first-match-wins. Mutually exclusive.
//
// Currently empty: every cohort/availability signal we'd want to surface is
// rendered on the FeedListingCard image overlay instead, leaving the tier
// badge clean:
//   - Price drop  -> PRICE DROP image badge + price strikethrough
//   - Top pick    -> TOP MATCH image badge (same per-model rank-1 set)
//   - Just listed -> JUST LISTED image badge (gated on listing_snapshot.dom)
//   - Best deal   -> BEST DEAL image badge (per-model rank-1 on price_vs_market)
//
// Kept as an extension point: re-add an entry here if a future signal can't
// fit on the image (e.g. "Trending", "Rare find", "Negotiable").
// -------------------------------------------------------------------

const MODIFIERS = [];

// -------------------------------------------------------------------
// Public API
// -------------------------------------------------------------------

/**
 * Resolve the right-column tag for one breakdown row.
 *
 * @param {string} signal       - Signal key (e.g. 'price_vs_market')
 * @param {number} value        - Engine's 0-100 value for the signal
 * @param {string} [explanation]- Engine's plain-language explanation string
 * @param {object} [snapshot]   - listing_snapshot (for fuel_type, num_owners, etc.)
 * @returns {{label: string, valence: 'positive'|'neutral'|'negative', informational?: boolean} | null}
 */
function getSignalTag(signal, value, explanation, snapshot) {
  if (!signal) return null;

  const resolver = SIGNAL_RESOLVERS[signal];
  if (resolver) return resolver(value, explanation, snapshot);

  const bands = SIGNAL_BANDS[signal];
  if (!bands) return null;

  for (const band of bands) {
    const matches = band.eq != null ? value === band.eq : value != null && value >= band.min;
    if (matches) {
      return {
        label: band.label,
        valence: band.valence,
        ...(band.informational ? { informational: true } : {}),
      };
    }
  }
  return null;
}

/**
 * Resolve the overall match badge: tier base + at most one modifier.
 *
 * @param {object} listing         - Feed entry with score, listing_snapshot, price_change_*
 * @param {object} [cohort]        - { topPickIds: Set, bestPriceIds: Set } from the surfaced feed
 * @returns {{tier: {key: string, label: string, min: number}, modifier: {key: string, label: string} | null}}
 */
function getOverallTag(listing, cohort) {
  const score = listing && listing.score != null ? Number(listing.score) : 0;
  const tier =
    TIER_BANDS.find((b) => score >= b.min) || TIER_BANDS[TIER_BANDS.length - 1];

  let modifier = null;
  for (const m of MODIFIERS) {
    if (m.test({ listing, cohort })) {
      modifier = { key: m.key, label: m.label };
      break;
    }
  }

  return { tier, modifier };
}

// -------------------------------------------------------------------
// Feed reorder — break up "Strong match" walls and promote modifier
// variety within tier runs.
// -------------------------------------------------------------------

/**
 * Reorder a fetched feed page to maximise visual variety:
 *   1. Cap consecutive same-tier cards (default max 2). When the cap would be
 *      breached, the next non-matching-tier card is pulled forward.
 *   2. Within each tier, "Best deal" cards (cohort.bestPriceIds) bubble to the
 *      front of their bucket. Same-score-order otherwise.
 *
 * Pure + deterministic: same input + same cohort always produces the same output,
 * which keeps the prefix stable across paginated re-renders.
 *
 * @param {Array} listings - Listings as fetched (already sorted by score DESC)
 * @param {Object} [cohort] - { bestPriceIds: Set }
 * @param {Object} [options]
 * @param {number} [options.maxConsecutive=2]
 * @returns {Array}
 */
function reorderListingsForVariety(listings, cohort, options = {}) {
  if (!Array.isArray(listings) || listings.length <= 2) return listings || [];
  const maxConsecutive = options.maxConsecutive ?? 2;

  const bestPriceIds = (cohort && cohort.bestPriceIds) || null;
  const tierOrder = TIER_BANDS.map((b) => b.key); // ['strong','solid','mixed','limited']
  const buckets = Object.fromEntries(tierOrder.map((k) => [k, []]));

  for (const listing of listings) {
    const { tier } = getOverallTag(listing, cohort);
    const key = (tier && tier.key) || 'limited';
    const id = listing.id || listing.feed_entry_id;
    const isBestDeal = !!(bestPriceIds && id != null && bestPriceIds.has(id));
    (buckets[key] || buckets.limited).push({ listing, isPromoted: isBestDeal });
  }

  // Stable sort: Best deal cards first within bucket; preserves score order otherwise.
  for (const key of tierOrder) {
    buckets[key].sort((a, b) => (a.isPromoted === b.isPromoted ? 0 : a.isPromoted ? -1 : 1));
  }

  const peek = (k) => buckets[k] && buckets[k].length > 0;
  const take = (k) => buckets[k].shift().listing;

  const result = [];
  let lastTier = null;
  let consecutive = 0;

  while (tierOrder.some(peek)) {
    // Default: highest-quality tier with items.
    let order = tierOrder;
    // Cap-breach guard: push the run-tier to the back so an alternative wins this slot.
    if (lastTier && consecutive >= maxConsecutive) {
      order = tierOrder.filter((t) => t !== lastTier).concat([lastTier]);
    }
    const chosen = order.find(peek);
    if (!chosen) break;

    result.push(take(chosen));
    if (chosen === lastTier) consecutive += 1;
    else { lastTier = chosen; consecutive = 1; }
  }
  return result;
}

export {
  getSignalTag,
  getOverallTag,
  reorderListingsForVariety,
  // Exported for tests + cohort computation upstream
  SIGNAL_BANDS,
  TIER_BANDS,
  MODIFIERS,
};
